import { useState, useEffect } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { AlertCircle, Plus, Trash2 } from 'lucide-react';
import { createFileRoute } from '@tanstack/react-router';
import SuperAdminLayout from '@/components/SuperAdminLayout';
import { apiFetch } from '@/utils/api';

interface SuperAdmin {
    id: string;
    email: string;
    full_name: string | null;
    is_active: boolean;
    last_login: string | null;
}

function SuperAdminsPage() {
    const navigate = useNavigate();
    const [superadmins, setSuperadmins] = useState<SuperAdmin[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState('');
    const [showNewForm, setShowNewForm] = useState(false);
    const [newAdminData, setNewAdminData] = useState({
        email: '',
        password: '',
        full_name: '',
    });
    const [isSubmitting, setIsSubmitting] = useState(false);

    useEffect(() => {
        fetchSuperadmins();
    }, []);

    const fetchSuperadmins = async () => {
        try {
            const response = await apiFetch('/api/superadmin/superadmins');

            if (!response.ok) {
                if (response.status === 401) {
                    navigate({ to: '/superadmin-login' });
                    return;
                }
                throw new Error('Failed to fetch superadmins');
            }

            const data = await response.json();
            setSuperadmins(data);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'An error occurred');
        } finally {
            setIsLoading(false);
        }
    };

    const handleCreateSuperadmin = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');
        setIsSubmitting(true);

        try {
            const response = await apiFetch('/api/superadmin/superadmins', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(newAdminData),
            });

            if (!response.ok) {
                const data = await response.json();
                setError(data.detail || 'Failed to create superadmin');
                return;
            }

            const data = await response.json();
            setSuperadmins([...superadmins, data]);
            setNewAdminData({ email: '', password: '', full_name: '' });
            setShowNewForm(false);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'An error occurred');
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleDeleteSuperadmin = async (adminId: string, email: string) => {
        if (!confirm(`Are you sure you want to delete ${email}?`)) return;

        try {
            const response = await apiFetch(`/api/superadmin/superadmins/${adminId}`, {
                method: 'DELETE',
            });

            if (!response.ok) {
                throw new Error('Failed to delete superadmin');
            }

            setSuperadmins(superadmins.filter(a => a.id !== adminId));
        } catch (err) {
            setError(err instanceof Error ? err.message : 'An error occurred');
        }
    };

    if (isLoading) {
        return (
            <SuperAdminLayout>
                <div className="space-y-6">
                    <h1 className="text-3xl font-bold tracking-tight">Superadmins</h1>
                    <div className="text-center py-12">
                        <p className="text-slate-600">Loading...</p>
                    </div>
                </div>
            </SuperAdminLayout>
        );
    }

    return (
        <SuperAdminLayout>
            <div className="space-y-6">
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-3xl font-bold tracking-tight">Superadmins</h1>
                    <p className="text-slate-600 mt-1">Manage system administrators</p>
                </div>
                <Button onClick={() => setShowNewForm(!showNewForm)} className="gap-2">
                    <Plus className="h-4 w-4" />
                    Add Superadmin
                </Button>
            </div>

            {error && (
                <Alert variant="destructive">
                    <AlertCircle className="h-4 w-4" />
                    <AlertDescription>{error}</AlertDescription>
                </Alert>
            )}

            {showNewForm && (
                <div className="bg-white rounded-lg border border-slate-200 p-6 max-w-2xl">
                    <h2 className="text-lg font-semibold mb-4">Create New Superadmin</h2>
                    <form onSubmit={handleCreateSuperadmin} className="space-y-4">
                        <div>
                            <label htmlFor="email" className="block text-sm font-medium text-slate-900 mb-1">
                                Email *
                            </label>
                            <Input
                                id="email"
                                type="email"
                                placeholder="admin@example.com"
                                value={newAdminData.email}
                                onChange={(e) => setNewAdminData({ ...newAdminData, email: e.target.value })}
                                disabled={isSubmitting}
                                required
                            />
                        </div>
                        <div>
                            <label htmlFor="password" className="block text-sm font-medium text-slate-900 mb-1">
                                Password *
                            </label>
                            <Input
                                id="password"
                                type="password"
                                placeholder="••••••••"
                                value={newAdminData.password}
                                onChange={(e) => setNewAdminData({ ...newAdminData, password: e.target.value })}
                                disabled={isSubmitting}
                                required
                                minLength={8}
                            />
                            <p className="text-xs text-slate-600 mt-1">Minimum 8 characters</p>
                        </div>
                        <div>
                            <label htmlFor="full_name" className="block text-sm font-medium text-slate-900 mb-1">
                                Full Name
                            </label>
                            <Input
                                id="full_name"
                                type="text"
                                placeholder="John Doe"
                                value={newAdminData.full_name}
                                onChange={(e) => setNewAdminData({ ...newAdminData, full_name: e.target.value })}
                                disabled={isSubmitting}
                            />
                        </div>
                        <div className="flex gap-2 pt-2">
                            <Button type="submit" disabled={isSubmitting}>
                                {isSubmitting ? 'Creating...' : 'Create'}
                            </Button>
                            <Button
                                type="button"
                                variant="outline"
                                onClick={() => setShowNewForm(false)}
                                disabled={isSubmitting}
                            >
                                Cancel
                            </Button>
                        </div>
                    </form>
                </div>
            )}

            <div className="bg-white rounded-lg border border-slate-200">
                {superadmins.length === 0 ? (
                    <div className="text-center py-12">
                        <p className="text-slate-600 mb-4">No superadmins yet</p>
                        <Button onClick={() => setShowNewForm(true)} variant="outline">
                            Create the first superadmin
                        </Button>
                    </div>
                ) : (
                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableHead>Email</TableHead>
                                <TableHead>Full Name</TableHead>
                                <TableHead>Status</TableHead>
                                <TableHead>Last Login</TableHead>
                                <TableHead className="text-right">Actions</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {superadmins.map((admin) => (
                                <TableRow key={admin.id}>
                                    <TableCell className="font-medium">{admin.email}</TableCell>
                                    <TableCell>{admin.full_name || '-'}</TableCell>
                                    <TableCell>
                                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                            admin.is_active
                                                ? 'bg-green-100 text-green-800'
                                                : 'bg-slate-100 text-slate-800'
                                        }`}>
                                            {admin.is_active ? 'Active' : 'Inactive'}
                                        </span>
                                    </TableCell>
                                    <TableCell className="text-slate-600">
                                        {admin.last_login ? new Date(admin.last_login).toLocaleDateString() : '-'}
                                    </TableCell>
                                    <TableCell className="text-right">
                                        <Button
                                            size="sm"
                                            variant="ghost"
                                            onClick={() => handleDeleteSuperadmin(admin.id, admin.email)}
                                            className="text-red-600 hover:text-red-700 h-8 w-8 p-0"
                                        >
                                            <Trash2 className="h-4 w-4" />
                                        </Button>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                )}
                </div>
                </div>
        </SuperAdminLayout>

    );
}

export const Route = createFileRoute('/superadmin/superadmins/')({
    component: SuperAdminsPage,
});
